/**
 * Mafalda Collection Manager - Script Único
 * Detecta mobile/desktop, rastreia cliques e salva posição de scroll
 */

class MalfaldaCollectionManager {
  constructor() {
    this.storageKey = 'mafalda_collection_state';
    this.currentCollection = this.getCollectionFromURL();
    this.isRestoring = false;
    this.hasRestored = false;
    
    this.isMobile = this.detectMobile();
    this.deviceType = this.isMobile ? 'mobile' : 'desktop';
    
    this.state = this.loadState() || {
      collection: this.currentCollection,
      clickCount: 0,
      scrollPosition: 0,
      productsCount: 0,
      deviceType: this.deviceType,
      timestamp: new Date().toISOString()
    };
  }

  detectMobile() {
    const isMobileViewport = window.innerWidth <= 768;
    
    const mobileAgents = [
      /Android/i, /webOS/i, /iPhone/i, /iPad/i, /iPod/i,
      /BlackBerry/i, /Windows Phone/i, /IEMobile/i, /Opera Mini/i
    ];
    const userAgent = navigator.userAgent;
    const isMobileAgent = mobileAgents.some(agent => agent.test(userAgent));
    
    return isMobileViewport || isMobileAgent;
  }

  getCollectionFromURL() {
    const match = window.location.pathname.match(/\/collections\/([^/]+)/);
    return match ? match[1] : null;
  }

  loadState() {
    try {
      const saved = sessionStorage.getItem(this.storageKey);
      
      if (saved) {
        const parsed = JSON.parse(saved);
        
        if (parsed.collection === this.currentCollection) {
          return parsed;
        }
      }
    } catch (e) {
      // Silent fail
    }
    return null;
  }

  saveState() {
    try {
      const state = {
        collection: this.currentCollection,
        clickCount: this.state.clickCount,
        scrollPosition: this.isRestoring ? this.state.scrollPosition : (window.scrollY || window.pageYOffset),
        productsCount: this.countProducts(),
        deviceType: this.deviceType,
        timestamp: new Date().toISOString()
      };
      
      sessionStorage.setItem(this.storageKey, JSON.stringify(state));
      this.state = state;
    } catch (e) {
      // Silent fail
    }
  }

  countProducts() {
    const selectors = [
      'hdt-card-product',
      '[data-product-id]',
      '.hdt-product-card',
      '.product-item',
      '[class*="product-card"]',
      'article[class*="product"]'
    ];

    for (const selector of selectors) {
      const elements = document.querySelectorAll(selector);
      if (elements.length > 0) {
        return elements.length;
      }
    }
    
    return 0;
  }

  restoreState() {
    if (!this.state || this.state.collection !== this.currentCollection) {
      return;
    }

    this.isRestoring = true;

    const restore = () => {
      const produtosSalvos = this.state.productsCount;
      const produtosAtuais = this.countProducts();

      if (produtosAtuais < produtosSalvos) {
        this.loadMissingProducts(produtosSalvos, () => {
          setTimeout(() => {
            this.finishRestore();
          }, 500);
        });
      } else {
        this.finishRestore();
      }
    };

    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', restore);
    } else {
      restore();
    }
  }

  loadMissingProducts(targetCount, onComplete) {
    let tentativas = 0;
    const maxTentativas = 30;
    
    const carregar = () => {
      tentativas++;
      const atual = this.countProducts();
      
      if (atual >= targetCount) {
        setTimeout(() => {
          this.scrollToPosition(this.state.scrollPosition);
          if (onComplete) onComplete();
        }, 300);
        return;
      }

      if (tentativas > maxTentativas) {
        setTimeout(() => {
          this.scrollToPosition(this.state.scrollPosition);
          if (onComplete) onComplete();
        }, 300);
        return;
      }

      const botao = document.querySelector('a.hdt-loamore-btn, [loadmore-btn-wrapp] a');

      if (botao && !botao.disabled) {
        botao.click();
        setTimeout(carregar, 1500);
      } else {
        setTimeout(() => {
          this.scrollToPosition(this.state.scrollPosition);
          if (onComplete) onComplete();
        }, 300);
      }
    };

    carregar();
  }

  trackLoadMoreClicks() {
    let lastClickTime = 0;
    document.addEventListener('click', (e) => {
      const botao = e.target.closest('a.hdt-loamore-btn, [loadmore-btn-wrapp] a');

      if (botao) {
        const now = Date.now();
        if (now - lastClickTime > 100) {
          lastClickTime = now;
          this.state.clickCount++;
          this.saveState();
        }
      }
    });
  }

  trackScroll() {
    let scrollTimeout;
    window.addEventListener('scroll', () => {
      if (this.isRestoring) {
        return;
      }
      
      clearTimeout(scrollTimeout);
      scrollTimeout = setTimeout(() => {
        this.saveState();
      }, 500);
    });
  }

  trackProductClick() {
    document.addEventListener('click', (e) => {
      const link = e.target.closest('a');
      if (!link) return;

      const href = link.getAttribute('href') || '';
      
      if (href.includes('/products/')) {
        this.saveState();
      }
    });
  }

  trackPageLifecycle() {
    window.addEventListener('pagehide', () => {
      if (!this.isRestoring) {
        this.saveState();
      }
    });

    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'hidden' && !this.isRestoring) {
        this.saveState();
      }
    });

    window.addEventListener('pageshow', (event) => {
      if (event.persisted || this.hasRestored) {
        this.state = this.loadState() || this.state;
        this.restoreState();
      }
    });
  }

  getDebugInfo() {
    return {
      collection: this.currentCollection,
      deviceType: this.deviceType,
      isMobile: this.isMobile,
      clickCount: this.state.clickCount,
      scrollPosition: this.state.scrollPosition,
      productsCount: this.state.productsCount,
      currentProducts: this.countProducts(),
      currentScroll: window.scrollY
    };
  }

  clearData() {
    sessionStorage.removeItem(this.storageKey);
    this.state = {
      collection: this.currentCollection,
      clickCount: 0,
      scrollPosition: 0,
      productsCount: 0,
      deviceType: this.deviceType,
      timestamp: new Date().toISOString()
    };
  }

  scrollToPosition(position) {
    const top = Number(position) || 0;
    window.scrollTo({ top, left: 0, behavior: 'auto' });
    document.documentElement.scrollTop = top;
    document.body.scrollTop = top;
  }

  finishRestore() {
    const position = this.state.scrollPosition;
    const delays = [0, 50, 150, 350, 700];

    delays.forEach((delay, index) => {
      setTimeout(() => {
        this.scrollToPosition(position);

        if (index === delays.length - 1) {
          this.isRestoring = false;
          this.hasRestored = true;
        }
      }, delay);
    });
  }

  initialize() {
    if (!this.currentCollection) {
      return;
    }

    this.trackLoadMoreClicks();
    this.trackScroll();
    this.trackProductClick();
    this.trackPageLifecycle();
    
    if ('scrollRestoration' in history) {
      history.scrollRestoration = 'manual';
    }

    this.restoreState();
  }
}

(function() {
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => {
      const manager = new MalfaldaCollectionManager();
      manager.initialize();
      window.malfaldaCollection = manager;
    });
  } else {
    const manager = new MalfaldaCollectionManager();
    manager.initialize();
    window.malfaldaCollection = manager;
  }
})();
